import {
  BatchMode,
  ExperienceTemplateMessage
} from '@dadohr/shared/domainTypes/experience';
import {
  Button,
  FormControlLabel,
  Radio,
  RadioGroup,
  Switch,
  TextField,
  Typography
} from '@material-ui/core';
import { ListChecks, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { CustomInputLabel } from '../../../../../../components/CustomInputLabel';
import { FormRow } from '../../../../../../components/Form/FormRow';
import { useUnsafeTranslation } from '../../../../../../i18next';
import { ONE_DAY } from '../../../../../../services/time';
import { TaskBuilderContext } from '../types';
import { SettingsSection, SideContainer } from './layout';
import { SideContainerHeader } from './SideContainerHeader';
import { TaskSettingsOverviewSection } from './TaskSettingsOverviewSection';

const ICON = ListChecks;

const DisabledHint = () => {
  const { t } = useUnsafeTranslation();
  return (
    <Typography variant="caption">
      {t(
        'Task Lists cannot be included in Messages sent once per cohort. This setting can be adjusted in the Sender and Recipient options.'
      )}
    </Typography>
  );
};

export const MessageSettingsOverviewSectionTaskList = ({
  title,
  value,
  onEdit,
  divider
}: {
  title: string;
  value: ExperienceTemplateMessage;
  onEdit?: () => void;
  divider?: boolean;
}) => {
  const {
    taskList: { range, variant },
    batchMode
  } = value;
  const { t } = useUnsafeTranslation();
  const canBeUsed = batchMode === BatchMode.DEFAULT;
  return (
    <TaskSettingsOverviewSection
      icon={ICON}
      title={title}
      onEdit={onEdit}
      divider={divider}
    >
      {canBeUsed ? (
        <>
          <div>
            {range === 'ALL'
              ? t('Include all future tasks')
              : range === 0
              ? t(`Don't include a task list`)
              : range === 1 * ONE_DAY
              ? t(`Include task starting on the message's send date`)
              : t(
                  `Include tasks {{ days }} days within this messages's send date`,
                  { days: Math.floor(range / ONE_DAY) }
                )}
          </div>
          {range !== 0 && (
            <div>
              {variant === 'expanded'
                ? t('Show full content for ongoing tasks')
                : t('Show title and deadline for ongoing tasks')}
            </div>
          )}
        </>
      ) : (
        <DisabledHint />
      )}
    </TaskSettingsOverviewSection>
  );
};

export const MessageSettingsTaskList = ({
  title,
  description,
  onBack,
  value,
  onChange
}: {
  title: string;
  description: string;

  onBack: () => void;

  value: ExperienceTemplateMessage;
  onChange: React.Dispatch<React.SetStateAction<ExperienceTemplateMessage>>;
  expTpl: TaskBuilderContext;
}) => {
  const { t } = useUnsafeTranslation();
  const includeTaskList = !!value.taskList.range;
  const { batchMode } = value;
  const canBeUsed = batchMode === BatchMode.DEFAULT;
  return (
    <SideContainer>
      <SideContainerHeader
        def={{
          label: title,
          description,
          icon: ICON
        }}
        onBack={onBack}
      />
      <SettingsSection>
        <FormRow>
          <FormControlLabel
            label={t('Include Task List')}
            disabled={!canBeUsed}
            control={
              <Switch
                checked={canBeUsed && includeTaskList}
                onChange={() => {
                  onChange((v) => ({
                    ...v,
                    taskList: {
                      range: includeTaskList ? 0 : 7 * ONE_DAY,
                      variant: value.taskList.variant
                    }
                  }));
                }}
                color="primary"
              />
            }
          />
        </FormRow>
        {!canBeUsed && <DisabledHint />}
        {!!value.taskList.range && canBeUsed && (
          <>
            <FormRow>
              <CustomInputLabel label={t('Include tasks')}>
                <RadioGroup
                  aria-label="Timespan"
                  value={`${
                    value.taskList.range === 'ALL'
                      ? value.taskList.range
                      : Math.floor(value.taskList.range / ONE_DAY)
                  }`}
                  onChange={(ev) => {
                    const v = ev.target.value as '1' | '7' | '30' | 'ALL';
                    onChange({
                      ...value,
                      taskList: {
                        range: v === 'ALL' ? v : parseInt(v) * ONE_DAY,
                        variant: value.taskList.variant
                      }
                    });
                  }}
                >
                  <FormControlLabel
                    value={'1'}
                    control={<Radio />}
                    label={t(`Starting on the same day`)}
                  />
                  <FormControlLabel
                    value={'7'}
                    control={<Radio />}
                    label={t(`Starting within 7 days`)}
                  />
                  <FormControlLabel
                    value={'30'}
                    control={<Radio />}
                    label={t(`Starting within 30 days`)}
                  />
                  <FormControlLabel
                    value={'ALL'}
                    control={<Radio />}
                    label={t(`All future tasks`)}
                  />
                </RadioGroup>
              </CustomInputLabel>
            </FormRow>
            <FormRow>
              <CustomInputLabel label={t('Task display style')}>
                <RadioGroup
                  aria-label="Timespan"
                  value={value.taskList.variant}
                  onChange={(ev) => {
                    const v = ev.target.value as 'expanded' | 'teaser';
                    onChange({
                      ...value,
                      taskList: { variant: v, range: value.taskList.range }
                    });
                  }}
                >
                  <FormControlLabel
                    value={'expanded'}
                    control={<Radio />}
                    label={t(`Show full task content`)}
                  />
                  <FormControlLabel
                    value={'teaser'}
                    control={<Radio />}
                    label={t(`Show titles and deadlines only`)}
                  />
                </RadioGroup>
              </CustomInputLabel>
            </FormRow>
          </>
        )}
      </SettingsSection>
    </SideContainer>
  );
};

type IncludeTasksMode = 'SAME_TIME' | 'WITHIN_DAYS' | 'ALL';

const getIncludeTasksMode = (range: number | 'ALL'): IncludeTasksMode => {
  if (range === 'ALL') return 'ALL';
  if (range === 1 * ONE_DAY) return 'SAME_TIME';
  return 'WITHIN_DAYS';
};

/**
 * NEW_UX Task List settings — reached by clicking the "Task list" block-picker button
 * (adds it with a default range on first click, opens this panel on subsequent clicks)
 * or by clicking the rendered task list in the message preview. Unlike classic's Switch
 * + 4-option radio (MessageSettingsTaskList above), there's no separate on/off toggle
 * here — removing it is an explicit action in this panel, and "within N days" is a plain
 * number entry rather than fixed 7/30-day presets.
 */
export const MessageSettingsTaskListNewUx = ({
  value,
  onChange,
  onBack
}: {
  value: ExperienceTemplateMessage;
  onChange: React.Dispatch<React.SetStateAction<ExperienceTemplateMessage>>;
  onBack: () => void;
}) => {
  const { t } = useUnsafeTranslation();
  const { variant } = value.taskList;
  // Tracked as its own state, seeded once from the incoming range, rather than
  // re-derived from range on every change: range===1*ONE_DAY is ambiguous between the
  // SAME_TIME preset and a user-chosen "within 1 day", so re-deriving on every render
  // would flip the selected radio back to SAME_TIME the moment the number field is
  // decremented to 1 — this way the explicitly chosen radio never drifts on its own.
  const [mode, setMode] = useState<IncludeTasksMode>(() =>
    getIncludeTasksMode(value.taskList.range)
  );
  const [withinDays, setWithinDays] = useState<number>(() =>
    getIncludeTasksMode(value.taskList.range) === 'WITHIN_DAYS'
      ? Math.max(1, Math.floor((value.taskList.range as number) / ONE_DAY))
      : 7
  );
  // The field's own displayed text, separate from the committed withinDays number —
  // lets the field sit temporarily empty (or "0", or anything else not yet a valid
  // day count) while focused, e.g. to delete the current value before typing a new
  // one, without every keystroke snapping it back to "1". Reconciled to a valid
  // number only on blur.
  const [withinDaysText, setWithinDaysText] = useState<string>(String(withinDays));

  const applyRange = (nextMode: IncludeTasksMode, days: number) =>
    onChange((v) => ({
      ...v,
      taskList: {
        ...v.taskList,
        range:
          nextMode === 'ALL' ? 'ALL' : nextMode === 'SAME_TIME' ? 1 * ONE_DAY : days * ONE_DAY
      }
    }));

  return (
    <SideContainer>
      <SideContainerHeader
        def={{ label: t('Task list'), icon: ICON }}
        onBack={onBack}
        newUx
      />
      <SettingsSection>
        <FormRow style={{ marginBottom: 32 }}>
          <CustomInputLabel label={t('Include')}>
            <RadioGroup
              aria-label="Timespan"
              value={mode}
              onChange={(ev) => {
                const nextMode = ev.target.value as IncludeTasksMode;
                setMode(nextMode);
                applyRange(nextMode, withinDays);
              }}
            >
              <FormControlLabel
                value="SAME_TIME"
                control={<Radio />}
                label={t('Tasks starting at the same time')}
                style={{ marginBottom: 8 }}
              />
              <FormControlLabel
                value="WITHIN_DAYS"
                control={<Radio />}
                label={
                  <span
                    onClick={(ev) => ev.stopPropagation()}
                    style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
                  >
                    {t('Tasks starting within')}
                    <TextField
                      type="number"
                      size="small"
                      variant="outlined"
                      inputProps={{ min: 1, style: { width: 48, textAlign: 'center' } }}
                      value={withinDaysText}
                      onChange={(ev) => {
                        const text = ev.target.value;
                        setWithinDaysText(text);
                        const n = parseInt(text, 10);
                        if (n >= 1) {
                          setWithinDays(n);
                          setMode('WITHIN_DAYS');
                          applyRange('WITHIN_DAYS', n);
                        }
                      }}
                      onBlur={() => {
                        setWithinDaysText(String(withinDays));
                      }}
                    />
                    {t('days')}
                  </span>
                }
                style={{ marginBottom: 8 }}
              />
              <FormControlLabel
                value="ALL"
                control={<Radio />}
                label={t('All future tasks')}
              />
            </RadioGroup>
          </CustomInputLabel>
        </FormRow>
        <FormRow style={{ marginBottom: 32 }}>
          <CustomInputLabel label={t('Display style')}>
            <RadioGroup
              aria-label="Timespan"
              value={variant}
              onChange={(ev) => {
                const v = ev.target.value as 'expanded' | 'teaser';
                onChange((val) => ({ ...val, taskList: { ...val.taskList, variant: v } }));
              }}
            >
              <FormControlLabel
                value={'expanded'}
                control={<Radio />}
                label={t(`Show full task content`)}
                style={{ marginBottom: 8 }}
              />
              <FormControlLabel
                value={'teaser'}
                control={<Radio />}
                label={t(`Show titles and deadlines only`)}
              />
            </RadioGroup>
          </CustomInputLabel>
        </FormRow>
        <FormRow>
          <Button
            variant="text"
            color="secondary"
            size="small"
            startIcon={<Trash2 size={16} />}
            onClick={() => {
              onChange((v) => ({ ...v, taskList: { ...v.taskList, range: 0 } }));
              onBack();
            }}
          >
            {t('Remove task list')}
          </Button>
        </FormRow>
      </SettingsSection>
    </SideContainer>
  );
};
